import React, { memo } from 'react';
import { Alert, Box, CircularProgress, Grid, Typography } from '@mui/material';
import AdminStatusPill from '../../../components/admin/AdminStatusPill';
import AdminSurface from '../../../components/admin/AdminSurface';
import type { EmailPipelineStatusCardProps } from '../../../types/features/emailPipeline';
import { formatDateTimeMMDDYYYY } from '../../../utils/formatUtils';
import { getStatusLabel } from '../../../utils/formatters/statusFormatters';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';
import {
  adminAlertSx,
  adminAlertToneSx,
  adminCardHeaderSx,
  adminCombineSx,
} from '../../../styles/adminComponentStyles';
import { getEmailPipelineStatusTone } from '../mappers/emailPipelineFormatters';

const metricLabelSx = {
  ...adminTypography.helper,
  color: adminColors.mutedText,
  mb: 0.25,
} as const;

const metricValueSx = {
  ...adminTypography.bodyStrong,
  color: adminColors.text,
  overflowWrap: 'anywhere',
} as const;

const EmailPipelineStatusCard: React.FC<EmailPipelineStatusCardProps> = ({
  model,
}) => {
  const gmailWatch = model.statusData?.gmail_watch;
  const displayStatus = gmailWatch?.needs_renewal ? 'expired' : gmailWatch?.status;

  return (
    <AdminSurface
      sx={{
        mb: 1.5,
        p: '12px',
        backgroundColor: '#eff6ff',
        border: '1px solid rgb(96 165 250 / 40%)',
      }}
    >
      {/* <Box sx={adminCardHeaderSx}>
        <Typography className="AdminCardHeader-title">Pipeline Status</Typography>
        <Typography className="AdminCardHeader-meta">Gmail watch</Typography>
      </Box> */}

      {model.statusLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : model.statusError ? (
        <Alert severity="error" sx={adminCombineSx(adminAlertSx, adminAlertToneSx.danger)}>
          {model.statusError}
        </Alert>
      ) : (
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={metricLabelSx}>Status</Typography>
            <AdminStatusPill
              label={getStatusLabel(displayStatus)}
              tone={getEmailPipelineStatusTone(displayStatus)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={metricLabelSx}>Expiration Timestamp</Typography>
            <Typography sx={metricValueSx}>{formatDateTimeMMDDYYYY(gmailWatch?.expiration)}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={metricLabelSx}>Current History ID</Typography>
            <Typography sx={metricValueSx}>{gmailWatch?.history_id ? `#${gmailWatch.history_id}` : '-'}</Typography>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Typography sx={metricLabelSx}>Last Renewal</Typography>
            <Typography sx={metricValueSx}>{formatDateTimeMMDDYYYY(gmailWatch?.last_ran_at)}</Typography>
          </Grid>
        </Grid>
      )}
    </AdminSurface>
  );
};

export default memo(EmailPipelineStatusCard);
